import { state, save, labelOf, mkCam, markCam, matchesSource, type ViewSource } from '@/store/state';
import { setWall, isOnWall, renderWall, applyGlobalQuality, refreshQualityLabels, setView } from './grid';
import { attachDragSource } from './drag';
import { candidates } from '@/scan/candidates';
import { probe } from '@/scan/probe';
import { listCctvCameras, cctvSupported, CCTV_REMEMBERED, absentCctvCam } from '@/config/cctv';
import { SITE_CODES, SITE_TH, SCAN, QUALITY_CYCLE } from '@/config';
import type { Cam, Quality } from '@/engine/types';
import { ICON } from '@/lib/icons';
import { $, $$, must, esc, p2, sleep } from '@/lib/dom';
import { toast } from './toast';
import * as bus from '@/lib/bus';

let panelEl: HTMLElement;
let idxMax: number = SCAN.idxMax;
const collapsed = new Set<string>();

interface ScanRun { total: number; done: number; found: number; fresh: number; run: boolean; ctrls: Set<AbortController>; }
let scan: ScanRun | null = null;

export function initPanel(): void {
  panelEl = must('#panel');
  panelEl.innerHTML = `
    <section class="scan" id="scanPanel">
      <div class="scan-in">
        <div class="scan-top"><b>สแกนหากล้อง</b><button class="x" id="scanClose" aria-label="ปิด">${ICON.x}</button></div>
        <div class="scan-lbl">รหัสจุด — เดาตาม CAM-{จุด}-NN</div>
        <div class="codes" id="codes"></div>
        <div class="scan-cfg"><label>เลขกล้องถึง</label><div class="stepper"><button id="idxDown">−</button><span class="mono" id="idxVal">${idxMax}</span><button id="idxUp">+</button></div></div>
        <div class="scan-run"><button class="scan-start" id="scanStart">เริ่มสแกน</button><button class="scan-stop" id="scanStop">หยุด</button></div>
        <div class="scan-prog" id="scanProg">พร้อมสแกน</div>
        <div class="pbar"><i id="scanBar"></i></div>
        <div class="addbox"><input id="addInput" placeholder="เพิ่มเอง: CAM-XXX-01 หรือ URL" spellcheck="false"><button id="addBtn" aria-label="เพิ่ม">${ICON.plus}</button></div>
      </div>
    </section>
    <div class="p-head"><span class="p-title">กล้อง</span><span class="cnt mono" id="onlineCount">0/0</span></div>
    <div class="p-view">
      ${cctvSupported() ? `<div class="seg seg--src" id="srcSeg" role="group" aria-label="แหล่งกล้อง"><button data-src="all">ทั้งหมด</button><button data-src="canteen">โรงอาหาร</button><button data-src="cctv">จราจร</button></div>` : ''}
      <div class="p-view-row">
        <button class="chip" id="onlineBtn" aria-pressed="false" title="ซ่อนกล้องที่ออฟไลน์/ถูกปลดออกจากวอลล์"><span class="dot live"></span>เฉพาะออนไลน์</button>
        <button class="chip" id="unlimBtn" aria-pressed="false" title="ให้ทุกกล้องบนจอถอดรหัสพร้อมกัน (ปกติจำกัดไว้)">ไม่จำกัดจำนวน</button>
      </div>
    </div>
    <div class="p-quality">คุณภาพรวม <button class="qbtn mono" id="globalQ" title="กดสลับคุณภาพทุกกล้อง"></button></div>
    <div class="search"><input id="search" placeholder="ค้นหากล้อง…" spellcheck="false"></div>
    <div class="acts"><button class="mini" id="selAll">เลือกทั้งหมด</button><button class="mini" id="clrAll">เอาออกทั้งหมด</button></div>
    ${cctvSupported()
      ? `<button class="mini cctv-load" id="loadCctv">＋ โหลดกล้องจราจรรอบสถาบัน (CCTV)</button>`
      : `<p class="cctv-note">กล้องจราจรดูได้เฉพาะตอนรันในเครื่อง (<code>npm run dev</code>) — ต้นทางไม่เปิดสตรีมให้เซิร์ฟเวอร์ภายนอก</p>`}
    <div class="list" id="list"></div>`;

  renderCodes();
  renderList();
  syncView();
  updateGlobalQ();

  $('#globalQ', panelEl)!.addEventListener('click', cycleGlobalQuality);
  $('#scanClose', panelEl)!.addEventListener('click', () => $('#scanPanel', panelEl)!.classList.remove('open'));
  $('#scanStart', panelEl)!.addEventListener('click', () => { if (!scan) void runScan(); });
  $('#scanStop', panelEl)!.addEventListener('click', stopScan);
  $('#idxUp', panelEl)!.addEventListener('click', () => { idxMax = Math.min(20, idxMax + 1); $('#idxVal', panelEl)!.textContent = String(idxMax); });
  $('#idxDown', panelEl)!.addEventListener('click', () => { idxMax = Math.max(1, idxMax - 1); $('#idxVal', panelEl)!.textContent = String(idxMax); });
  const addInput = $('#addInput', panelEl) as HTMLInputElement;
  $('#addBtn', panelEl)!.addEventListener('click', () => { addManual(addInput.value); addInput.value = ''; });
  addInput.addEventListener('keydown', (e) => { if (e.key === 'Enter') { addManual(addInput.value); addInput.value = ''; } });
  $('#loadCctv', panelEl)?.addEventListener('click', () => void loadCctv());
  $('#search', panelEl)!.addEventListener('input', renderList);
  $('#selAll', panelEl)!.addEventListener('click', () => {
    for (const c of Object.values(state.cameras)) if (matchesSource(c)) setWall(c.id, true);
  });
  $('#srcSeg', panelEl)?.addEventListener('click', (e) => {
    const b = (e.target as HTMLElement).closest<HTMLElement>('button');
    if (b?.dataset['src']) setView({ source: b.dataset['src'] as ViewSource });
  });
  $('#onlineBtn', panelEl)!.addEventListener('click', () => setView({ onlineOnly: !state.view.onlineOnly }));
  $('#unlimBtn', panelEl)!.addEventListener('click', () => {
    const on = !state.view.unlimited;
    setView({ unlimited: on });
    if (on) toast('ไม่จำกัดจำนวน — ทุกกล้องบนจอจะโหลดพร้อมกัน อาจหน่วงถ้ามีเยอะ', 'info');
  });
  $('#clrAll', panelEl)!.addEventListener('click', () => { state.onWall = state.layout === 'fit' ? [] : state.onWall.map(() => null); save(); renderWall(); renderList(); });

  bus.on('wall:changed', syncRows);
  bus.on('view:changed', () => { syncView(); renderList(); });
  bus.on('cam:status', updateDot);
  bus.on('scan:stop', stopScan);

  autoSyncCctv();
}

export function toggleScanPanel(): void { $('#scanPanel', panelEl)!.classList.toggle('open'); }

// ---- global quality ----
function updateGlobalQ(): void { ($('#globalQ', panelEl) as HTMLButtonElement).textContent = state.globalQuality; }
function cycleGlobalQuality(): void {
  const i = QUALITY_CYCLE.indexOf(state.globalQuality as never);
  state.globalQuality = QUALITY_CYCLE[(i + 1) % QUALITY_CYCLE.length]! as Quality;
  save();
  updateGlobalQ();
  applyGlobalQuality();
}

// ---- camera list ----
function groupCams(): [string, Cam[]][] {
  const g: Record<string, Cam[]> = {};
  for (const c of Object.values(state.cameras)) {
    if (!matchesSource(c)) continue;
    const k = c.site ?? 'อื่น ๆ';
    (g[k] ??= []).push(c);
  }
  // aegis cams sort by numeric index; CCTV cams (no idx) sort by their id.
  for (const arr of Object.values(g)) arr.sort((a, b) => (a.idx && b.idx ? a.idx.localeCompare(b.idx) : a.id.localeCompare(b.id)));
  return Object.entries(g).sort((a, b) => a[0].localeCompare(b[0]));
}

export function renderList(): void {
  const list = must('#list', panelEl);
  const q = (($('#search', panelEl) as HTMLInputElement)?.value ?? '').toLowerCase().trim();
  list.innerHTML = '';
  let shown = 0;
  for (const [site, cams] of groupCams()) {
    const vis = cams.filter((c) => !q || c.id.toLowerCase().includes(q) || labelOf(c).toLowerCase().includes(q));
    if (!vis.length) continue;
    const th = SITE_TH[site] ?? site;
    const on = vis.filter((c) => isOnWall(c.id)).length;
    const g = document.createElement('div');
    g.className = 'grp' + (collapsed.has(site) ? ' collapsed' : '');
    g.innerHTML = `<div class="grp-head"><span class="chev">${ICON.chev}</span><span>${esc(th)}</span><span class="gc">${on}/${vis.length}</span></div><div class="rows"></div>`;
    g.querySelector('.grp-head')!.addEventListener('click', () => {
      if (collapsed.has(site)) collapsed.delete(site); else collapsed.add(site);
      g.classList.toggle('collapsed');
    });
    const rows = g.querySelector('.rows')!;
    for (const c of vis) {
      const row = document.createElement('div');
      row.className = 'row panel-row' + (isOnWall(c.id) ? ' on' : '');
      row.dataset['id'] = c.id;
      row.innerHTML = `<span class="dot ${c.status}"></span><span class="tgl"></span><span class="cam-name">${esc(labelOf(c))}</span><span class="cam-id mono">${c.id}</span><button class="row-del" title="ลบกล้อง" aria-label="ลบ">${ICON.trash}</button>`;
      row.querySelector('.row-del')!.addEventListener('click', (e) => { e.stopPropagation(); deleteCam(c.id); });
      attachDragSource(row, () => ({ kind: 'panel', cam: c.id }));
      rows.appendChild(row);
      shown++;
    }
    list.appendChild(g);
  }
  if (!shown) {
    const e = document.createElement('div');
    e.className = 'empty-list';
    e.textContent = q ? 'ไม่พบกล้องที่ค้นหา' : 'ยังไม่มีกล้อง — กดสแกน';
    list.appendChild(e);
  }
  updateOnline();
}

function syncRows(): void {
  for (const row of $$('.row', panelEl)) row.classList.toggle('on', isOnWall(row.dataset['id'] ?? ''));
  for (const g of $$('.grp', panelEl)) {
    const rows = $$('.row', g);
    const on = rows.filter((r) => r.classList.contains('on')).length;
    const gc = $('.gc', g);
    if (gc) gc.textContent = `${on}/${rows.length}`;
  }
  updateOnline();
}

function updateDot(id: string): void {
  const cam = state.cameras[id];
  const dot = $(`.row[data-id="${id}"] .dot`, panelEl);
  if (cam && dot) dot.className = `dot ${cam.status}`;
  updateOnline();
}

/** Reflect the view filter in the panel controls. */
function syncView(): void {
  for (const b of $$('#srcSeg button', panelEl)) b.classList.toggle('on', b.dataset['src'] === state.view.source);
  $('#onlineBtn', panelEl)?.setAttribute('aria-pressed', String(state.view.onlineOnly));
  $('#unlimBtn', panelEl)?.setAttribute('aria-pressed', String(state.view.unlimited));
}

function updateOnline(): void {
  const all = Object.values(state.cameras).filter((c) => matchesSource(c));
  const on = all.filter((c) => c.status === 'live' || c.status === 'online').length;
  const el = $('#onlineCount', panelEl);
  if (el) el.textContent = `${on}/${all.length}`;
}

// ---- delete + undo ----
function deleteCam(id: string): void {
  const cam = state.cameras[id];
  if (!cam) return;
  const idx = state.onWall.indexOf(id);
  const snap = { cam: JSON.parse(JSON.stringify(cam)) as Cam, idx, label: state.labels[id], quality: state.quality[id] };
  bus.emit('cam:deleted', id);
  delete state.cameras[id];
  state.onWall = state.layout === 'fit' ? state.onWall.filter((x) => x !== id) : state.onWall.map((x) => (x === id ? null : x));
  delete state.labels[id];
  delete state.quality[id];
  save();
  renderList();
  renderWall();
  toast(`ลบ ${id} แล้ว`, 'del', {
    label: 'เลิกทำ',
    fn: () => {
      state.cameras[snap.cam.id] = snap.cam;
      if (snap.label) state.labels[snap.cam.id] = snap.label;
      if (snap.quality) state.quality[snap.cam.id] = snap.quality;
      if (snap.idx >= 0) {
        if (state.layout === 'fit') state.onWall.splice(snap.idx, 0, snap.cam.id);
        else if (state.onWall[snap.idx] == null) state.onWall[snap.idx] = snap.cam.id;
        else state.onWall.push(snap.cam.id);
      }
      save();
      renderList();
      renderWall();
      toast('คืนค่าแล้ว', 'ok');
    },
  });
}

// ---- add manual ----
function addManual(raw: string): void {
  const v = raw.trim();
  if (!v) return;
  let id: string;
  let url: string | null = null;
  if (/^https?:\/\//i.test(v)) {
    url = v;
    const m = /\/hls\/([^/]+)\//.exec(v);
    id = m ? m[1]! : `CAM-CUSTOM-${p2((Object.keys(state.cameras).length % 99) + 1)}`;
  } else {
    id = v.toUpperCase().replace(/\s+/g, '');
  }
  if (state.cameras[id]) { setWall(id, true); flashRow(id); toast('มีกล้องนี้อยู่แล้ว'); return; }
  state.cameras[id] = mkCam(id, url, 'unknown');
  save();
  renderList();
  setWall(id, true);
  flashRow(id);
  toast(`เพิ่ม ${id} แล้ว`, 'ok');
}

// ---- load YooNee campus CCTV ----
/** Merge the live catalogue into state (adds new, prunes retired). */
async function syncCctv(): Promise<{ added: number; retired: number; returned: number; total: number }> {
  const cams = await listCctvCameras();
  let added = 0;
  for (const cam of cams) {
    const existing = state.cameras[cam.id];
    if (existing) {
      // refresh metadata but keep live status/lastSeen
      existing.provider = 'yoonee';
      existing.site = cam.site;
      existing.name = cam.name;
      existing.lat = cam.lat;
      existing.lng = cam.lng;
      existing.note = cam.note;
    } else {
      state.cameras[cam.id] = cam;
      added++;
    }
    if (cam.name && !state.labels[cam.id]) state.labels[cam.id] = cam.name;
  }
  // Cameras the catalogue no longer lists have been retired upstream and their
  // stream 404s — but keep them, in place and on the wall, marked 'absent'. The
  // catalogue churns, and when one returns it simply starts playing again on the
  // next attach with its slot and label intact. The tile's own backoff caps the
  // doomed retries at a handful, so holding them costs little.
  // Bring back cameras a previous version deleted, so the list spans everything
  // we have ever seen upstream rather than only what is live this minute.
  for (const r of CCTV_REMEMBERED) {
    if (state.cameras[r.id]) continue;
    state.cameras[r.id] = absentCctvCam(r.id, r.name);
    if (r.name && !state.labels[r.id]) state.labels[r.id] = r.name;
  }

  const live = new Set(cams.map((c) => c.id));
  let retired = 0;
  let returned = 0;
  for (const cam of Object.values(state.cameras)) {
    if (cam.provider !== 'yoonee') continue;
    if (live.has(cam.id)) {
      if (cam.status === 'absent') { markCam(cam, 'unknown'); returned++; }
    } else if (cam.status !== 'absent') {
      markCam(cam, 'absent');
      retired++;
    }
  }
  save();
  renderList();
  return { added, retired, returned, total: cams.length };
}

/** Forget every CCTV camera (used on hosts where the stream cannot be reached). */
function dropAllCctv(): void {
  let n = 0;
  for (const cam of Object.values(state.cameras)) {
    if (cam.provider !== 'yoonee') continue;
    bus.emit('cam:deleted', cam.id);
    delete state.cameras[cam.id];
    delete state.labels[cam.id];
    delete state.quality[cam.id];
    state.onWall = state.layout === 'fit'
      ? state.onWall.filter((x) => x !== cam.id)
      : state.onWall.map((x) => (x === cam.id ? null : x));
    n++;
  }
  if (!n) return;
  save();
  renderList();
  renderWall();
}

async function loadCctv(): Promise<void> {
  const btn = $('#loadCctv', panelEl) as HTMLButtonElement | null;
  const label = btn?.textContent ?? '';
  if (btn) { btn.disabled = true; btn.textContent = 'กำลังโหลด…'; }
  try {
    const { added, retired, returned, total } = await syncCctv();
    const msg = [
      added ? `เพิ่ม ${added}` : '',
      returned ? `กลับมา ${returned}` : '',
      retired ? `ปลดจากระบบ ${retired} (เก็บไว้)` : '',
    ].filter(Boolean).join(' · ');
    toast(msg || `อัปเดตกล้องจราจร ${total} ตัว`, added || returned || retired ? 'ok' : 'info');
  } catch {
    toast('โหลดกล้องจราจรไม่สำเร็จ — ตรวจการเชื่อมต่อ', 'warn');
  } finally {
    if (btn) { btn.disabled = false; btn.textContent = label; }
  }
}

/**
 * Boot reconcile. The upstream catalogue rotates (cameras get retired), and a
 * stale id streams as a 404 forever, so re-sync on load whenever we already hold
 * CCTV cameras. Quiet unless something actually changed; failure keeps state.
 */
function autoSyncCctv(): void {
  if (!Object.values(state.cameras).some((c) => c.provider === 'yoonee')) return;
  // Where CCTV cannot play (the deployed site), drop the cameras rather than let
  // every tile sit there retrying a stream the origin will not serve from here.
  if (!cctvSupported()) { dropAllCctv(); return; }
  void syncCctv()
    .then(({ added, retired, returned }) => {
      if (returned) toast(`กล้องจราจรกลับมา ${returned} ตัว`, 'ok');
      else if (added) toast(`พบกล้องจราจรใหม่ ${added} ตัว`, 'ok');
      else if (retired) toast(`กล้องจราจร ${retired} ตัวถูกปลดจากระบบ (เก็บไว้เผื่อกลับมา)`, 'info');
    })
    .catch(() => { /* upstream down — keep what we have */ });
}

function flashRow(id: string): void {
  const r = $(`.row[data-id="${id}"]`, panelEl);
  if (!r) return;
  r.style.background = 'var(--accent-weak)';
  setTimeout(() => (r.style.background = ''), 700);
  r.scrollIntoView({ block: 'nearest' });
}

// ---- scanner ----
function renderCodes(): void {
  const wrap = must('#codes', panelEl);
  wrap.innerHTML = '';
  for (const cc of SITE_CODES) {
    const on = state.sites.includes(cc.c);
    const b = document.createElement('button');
    b.className = 'code' + (on ? ' on' : '');
    b.textContent = cc.c;
    b.title = SITE_TH[cc.c] ?? cc.c;
    b.addEventListener('click', () => {
      state.sites = state.sites.includes(cc.c) ? state.sites.filter((x) => x !== cc.c) : [...state.sites, cc.c];
      b.classList.toggle('on');
      save();
    });
    wrap.appendChild(b);
  }
}

function addFound(id: string, status: Cam['status']): boolean {
  const fresh = !state.cameras[id];
  if (fresh) state.cameras[id] = mkCam(id, null, status);
  else markCam(state.cameras[id]!, status);
  state.cameras[id]!.lastSeen = Date.now();
  if (fresh) renderList();
  save();
  return fresh;
}

function setProg(done = false): void {
  const p = $('#scanProg', panelEl);
  const bar = $('#scanBar', panelEl) as HTMLElement | null;
  if (!p || !bar) return;
  if (!scan) { p.textContent = done ? 'สแกนเสร็จ' : 'พร้อมสแกน'; bar.style.width = done ? '100%' : '0'; return; }
  p.textContent = `ตรวจ ${scan.done}/${scan.total} · พบ ${scan.found}`;
  bar.style.width = `${((scan.done / scan.total) * 100).toFixed(1)}%`;
}

async function runScan(): Promise<void> {
  const sites = state.sites.slice();
  if (!sites.length) { const p = $('#scanProg', panelEl); if (p) p.textContent = 'เลือกรหัสจุดอย่างน้อย 1'; return; }
  const cands = candidates(sites, idxMax);
  scan = { total: cands.length, done: 0, found: 0, fresh: 0, run: true, ctrls: new Set() };
  $('#scanPanel', panelEl)!.classList.add('running');
  setProg();
  let i = 0;
  const worker = async (): Promise<void> => {
    while (scan && scan.run && i < cands.length) {
      const id = cands[i++]!;
      const res = await probe(id, SCAN.timeout, (ac) => scan?.ctrls.add(ac));
      if (!scan) break;
      scan.done++;
      state.scanCache[id] = { status: res, at: Date.now() };
      if (res === 'live' || res === 'exists') { scan.found++; if (addFound(id, res === 'live' ? 'live' : 'online')) scan.fresh++; }
      else if (state.cameras[id] && res === 'absent') markCam(state.cameras[id]!, 'offline');
      setProg();
      await sleep(25 + Math.random() * 45);
    }
  };
  await Promise.all(Array.from({ length: SCAN.concurrency }, worker));
  const fresh = scan ? scan.fresh : 0;
  scan = null;
  $('#scanPanel', panelEl)!.classList.remove('running');
  state.lastScan = Date.now();
  save();
  setProg(true);
  renderList();
  refreshQualityLabels();
  toast(fresh ? `พบกล้องใหม่ ${fresh} ตัว` : 'สแกนเสร็จ · ไม่พบกล้องใหม่', fresh ? 'ok' : 'info');
}

function stopScan(): void {
  if (!scan) return;
  scan.run = false;
  scan.ctrls.forEach((ac) => { try { ac.abort(); } catch { /* noop */ } });
}
